/**
 * Firestore rejects nested arrays (e.g. Fabric path data: [['M',0,0],['L',1,1]]).
 * Encode those as a single-string marker object on write; restore on read.
 */

export const NESTED_ARRAY_KEY = '__na';

function isPlainObject(value: unknown): value is Record<string, unknown> {
  if (value === null || typeof value !== 'object' || Array.isArray(value)) {
    return false;
  }
  const proto = Object.getPrototypeOf(value);
  return proto === Object.prototype || proto === null;
}

/** Deep-encode values so Firestore setDoc/updateDoc accept them. */
export function encodeFirestoreValue(value: unknown): unknown {
  if (value === null || typeof value !== 'object') {
    return value;
  }

  if (Array.isArray(value)) {
    if (value.some((item) => Array.isArray(item))) {
      return { [NESTED_ARRAY_KEY]: JSON.stringify(value) };
    }
    return value.map(encodeFirestoreValue);
  }

  if (!isPlainObject(value)) {
    return value;
  }

  const out: Record<string, unknown> = {};
  for (const [key, child] of Object.entries(value)) {
    if (child === undefined) continue;
    out[key] = encodeFirestoreValue(child);
  }
  return out;
}

/** Reverse {@link encodeFirestoreValue} after reading from Firestore. */
export function decodeFirestoreValue(value: unknown): unknown {
  if (value === null || typeof value !== 'object') {
    return value;
  }

  if (Array.isArray(value)) {
    return value.map(decodeFirestoreValue);
  }

  if (!isPlainObject(value)) {
    return value;
  }

  if (
    Object.keys(value).length === 1 &&
    typeof value[NESTED_ARRAY_KEY] === 'string'
  ) {
    try {
      return JSON.parse(value[NESTED_ARRAY_KEY] as string);
    } catch {
      return [];
    }
  }

  const out: Record<string, unknown> = {};
  for (const [key, child] of Object.entries(value)) {
    out[key] = decodeFirestoreValue(child);
  }
  return out;
}
